import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Plus, Pencil, Trash2, Folder, Check, X } from 'lucide-react';
import {
    getUtilityTemplateCategories,
    createUtilityTemplateCategory,
    renameUtilityTemplateCategory,
    deleteUtilityTemplateCategory,
} from '@/services/api/utility-template-categories';

interface ManageTemplateCategoriesDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * CRUD de categorias/pastas livres de utility template — sem hierarquia, sem
 * vínculo a país. Excluir uma categoria não exclui os grupos associados
 * (soft unlink no backend); aqui só invalidamos a lista de grupos para
 * refletir a remoção da associação.
 */
export default function ManageTemplateCategoriesDialog({
    open,
    onOpenChange,
}: ManageTemplateCategoriesDialogProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [newName, setNewName] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState('');
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const { data: categories = [], isLoading } = useQuery({
        queryKey: ['utility-template-categories'],
        queryFn: getUtilityTemplateCategories,
        enabled: open,
        staleTime: 1000 * 30,
    });

    const invalidateAll = () => {
        queryClient.invalidateQueries({ queryKey: ['utility-template-categories'] });
        queryClient.invalidateQueries({ queryKey: ['utility-template-groups'] });
    };

    const { mutate: create, isPending: creating } = useMutation({
        mutationFn: (name: string) => createUtilityTemplateCategory(name),
        onSuccess: () => {
            setNewName('');
            invalidateAll();
        },
        onError: () => {
            toast({ title: 'Erro ao criar categoria.', variant: 'destructive' });
        },
    });

    const { mutate: rename, isPending: renaming } = useMutation({
        mutationFn: ({ id, name }: { id: number; name: string }) => renameUtilityTemplateCategory(id, name),
        onSuccess: () => {
            setEditingId(null);
            setEditingName('');
            invalidateAll();
        },
        onError: () => {
            toast({ title: 'Erro ao renomear categoria.', variant: 'destructive' });
        },
    });

    const { mutate: remove, isPending: deleting } = useMutation({
        mutationFn: (id: number) => deleteUtilityTemplateCategory(id),
        onSuccess: () => {
            setDeletingId(null);
            toast({ title: 'Categoria excluída.', description: 'Os templates associados não foram afetados.' });
            invalidateAll();
        },
        onError: () => {
            toast({ title: 'Erro ao excluir categoria.', variant: 'destructive' });
        },
    });

    const handleCreate = () => {
        const trimmed = newName.trim();
        if (!trimmed || creating) return;
        create(trimmed);
    };

    const startEditing = (id: number, currentName: string) => {
        setEditingId(id);
        setEditingName(currentName);
    };

    const handleRename = (id: number) => {
        const trimmed = editingName.trim();
        if (!trimmed || renaming) return;
        rename({ id, name: trimmed });
    };

    const handleClose = () => {
        setNewName('');
        setEditingId(null);
        setEditingName('');
        setDeletingId(null);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Gerenciar categorias</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="flex items-center gap-2">
                        <Input
                            placeholder="Nome da nova categoria..."
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleCreate();
                            }}
                            disabled={creating}
                        />
                        <Button type="button" onClick={handleCreate} disabled={!newName.trim() || creating}>
                            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        </Button>
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="h-5 w-5 animate-spin text-cyan-500" />
                        </div>
                    ) : categories.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-6">
                            Nenhuma categoria criada ainda.
                        </p>
                    ) : (
                        <div className="flex flex-col gap-1.5 max-h-80 overflow-y-auto pr-1">
                            {categories.map((cat) => (
                                <div
                                    key={cat.id}
                                    className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2"
                                >
                                    {editingId === cat.id ? (
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <Input
                                                autoFocus
                                                value={editingName}
                                                onChange={(e) => setEditingName(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleRename(cat.id);
                                                    if (e.key === 'Escape') setEditingId(null);
                                                }}
                                                className="h-8 text-sm"
                                                disabled={renaming}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleRename(cat.id)}
                                                disabled={!editingName.trim() || renaming}
                                                className="shrink-0 text-emerald-600 hover:text-emerald-700 disabled:opacity-40"
                                                title="Salvar"
                                            >
                                                {renaming ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Check className="h-4 w-4" />
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setEditingId(null)}
                                                className="shrink-0 text-gray-400 hover:text-gray-600"
                                                title="Cancelar"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-center gap-2 min-w-0">
                                                <Folder className="h-4 w-4 text-gray-400 shrink-0" />
                                                <span className="text-sm text-gray-900 truncate">{cat.name}</span>
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => startEditing(cat.id, cat.name)}
                                                    className="p-1.5 text-gray-400 hover:text-cyan-600 rounded-md hover:bg-gray-50"
                                                    title="Renomear"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                                {deletingId === cat.id ? (
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => remove(cat.id)}
                                                            disabled={deleting}
                                                            className="text-[11px] font-medium text-red-600 hover:text-red-700 px-1.5 py-1 rounded-md hover:bg-red-50"
                                                        >
                                                            {deleting ? 'Excluindo...' : 'Confirmar'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setDeletingId(null)}
                                                            className="text-[11px] text-gray-400 hover:text-gray-600 px-1.5 py-1"
                                                        >
                                                            Cancelar
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => setDeletingId(cat.id)}
                                                        className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-50"
                                                        title="Excluir"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={handleClose}>
                        Fechar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
